import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface CoordinatesValue {
  lat: number;
  lng: number;
  label: string;
  areaLat: number;
  areaLng: number;
  areaLabel: string;
  precision: 'precise' | 'neighborhood';
}

interface Props {
  value: CoordinatesValue | null | undefined;
  onChange: (v: CoordinatesValue | null) => void;
  onSyncLocation?: (label: string) => void;
  locationContext?: { location?: string; city?: string; neighborhood?: string };
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  address: Record<string, string>;
  boundingbox: string[];
}

async function geocode(query: string): Promise<CoordinatesValue | null> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=1`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
  if (!res.ok) return null;
  const data: NominatimResult[] = await res.json();
  if (!data.length) return null;

  const r = data[0];
  const lat = parseFloat(r.lat);
  const lng = parseFloat(r.lon);
  const bb = r.boundingbox;
  const areaLat = (parseFloat(bb[0]) + parseFloat(bb[1])) / 2;
  const areaLng = (parseFloat(bb[2]) + parseFloat(bb[3])) / 2;

  const addr = r.address ?? {};
  const parts = [
    addr.suburb ?? addr.neighbourhood ?? addr.quarter,
    addr.city_district,
    addr.city ?? addr.town ?? addr.municipality ?? addr.county,
    addr.state,
  ].filter(Boolean) as string[];
  const unique = [...new Set(parts)].slice(0, 3);

  return {
    lat, lng,
    label: r.display_name,
    areaLat, areaLng,
    areaLabel: unique.join(', ') || r.display_name,
    precision: 'precise',
  };
}

function makePinIcon() {
  return L.divIcon({
    className: '',
    html: `<svg width="22" height="34" viewBox="0 0 22 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 0C4.925 0 0 4.925 0 11c0 8.25 11 23 11 23s11-14.75 11-23C22 4.925 17.075 0 11 0z" fill="#6366f1"/>
      <circle cx="11" cy="11" r="4" fill="white"/>
    </svg>`,
    iconSize: [22, 34],
    iconAnchor: [11, 34],
  });
}

export default function CoordinatesInput({ value, onChange, onSyncLocation, locationContext }: Props) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  // Always-current refs to avoid stale closures in Leaflet event handlers
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  // Pre-arma la query con los campos de ubicación del form si aún no hay coordenadas.
  useEffect(() => {
    if (value || !locationContext) return;
    const { neighborhood, city, location } = locationContext;
    const parts = [neighborhood, city].filter(Boolean) as string[];
    const q = parts.length ? parts.join(', ') : (location ?? '');
    if (q) setQuery(q);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Inicializa o actualiza el mapa Leaflet cuando cambia el valor.
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || !value) return;

    if (!mapRef.current) {
      const lat = value.precision === 'precise' ? value.lat : value.areaLat;
      const lng = value.precision === 'precise' ? value.lng : value.areaLng;
      const zoom = value.precision === 'precise' ? 16 : 14;

      const map = L.map(container, {
        center: [lat, lng],
        zoom,
        scrollWheelZoom: false,
        attributionControl: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);
      // Forzar layout correcto tras animación de entrada
      setTimeout(() => map.invalidateSize(), 120);
      mapRef.current = map;
    }

    const map = mapRef.current;

    if (value.precision === 'precise') {
      // Quitar círculo si existía
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }

      const cur = markerRef.current?.getLatLng();
      const moved = !cur || Math.abs(cur.lat - value.lat) > 0.000001 || Math.abs(cur.lng - value.lng) > 0.000001;
      if (moved) map.setView([value.lat, value.lng], 16, { animate: true });

      if (!markerRef.current) {
        const m = L.marker([value.lat, value.lng], { draggable: true, icon: makePinIcon() }).addTo(map);
        m.on('dragend', () => {
          const pos = m.getLatLng();
          const v = valueRef.current;
          if (!v) return;
          onChangeRef.current({ ...v, lat: pos.lat, lng: pos.lng });
        });
        markerRef.current = m;
      } else if (moved) {
        markerRef.current.setLatLng([value.lat, value.lng]);
      }
    } else {
      // Quitar pin si existía
      if (markerRef.current) { map.removeLayer(markerRef.current); markerRef.current = null; }

      if (!circleRef.current) {
        const c = L.circle([value.areaLat, value.areaLng], {
          radius: 600,
          color: '#6366f1',
          fillColor: '#6366f1',
          fillOpacity: 0.12,
          weight: 2,
        }).addTo(map);
        circleRef.current = c;
      } else {
        circleRef.current.setLatLng([value.areaLat, value.areaLng]);
      }
      map.fitBounds(circleRef.current.getBounds(), { animate: true, padding: [20, 20] });
    }
  }, [value]);

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
  }, []);

  async function handleSearch() {
    if (!query.trim()) return;
    setSearching(true);
    setNotFound(false);
    const result = await geocode(query.trim());
    setSearching(false);
    if (!result) { setNotFound(true); return; }
    onChange(result);
    onSyncLocation?.(result.areaLabel);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
  }

  function setPrecision(p: 'precise' | 'neighborhood') {
    if (!value) return;
    onChange({ ...value, precision: p });
  }

  function parseCoord(raw: string): number | null {
    const n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  const hasValue = value && (value.lat || value.lng);

  return (
    <div className="flex flex-col gap-3">
      {/* Search row */}
      <div className="flex gap-2">
        <div className="flex-1 flex items-center bg-ui-bg border border-ui-border rounded focus-within:border-ui-accent transition-colors px-3 gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text-subtle shrink-0" aria-hidden="true">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setNotFound(false); }}
            onKeyDown={handleKeyDown}
            placeholder="Buscar dirección del inmueble..."
            className="flex-1 bg-transparent text-sm text-ui-text py-2 focus:outline-none placeholder:text-ui-text-subtle"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          className="px-3 py-2 text-xs bg-ui-accent hover:bg-ui-accent-hover disabled:opacity-40 text-ui-text-inverse rounded transition-colors shrink-0"
        >
          {searching ? '…' : 'Buscar'}
        </button>
      </div>

      {notFound && (
        <p className="text-xs text-amber-400">No se encontró la dirección. Intenta con más detalle o edita las coordenadas manualmente.</p>
      )}

      <AnimatePresence>
        {hasValue && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col gap-3"
          >
            {/* Found address */}
            <div className="flex items-start gap-2 px-3 py-2.5 bg-ui-surface rounded border border-ui-border-strong">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-accent mt-0.5 shrink-0" aria-hidden="true">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <span className="text-xs text-ui-text-muted leading-snug">{value!.label}</span>
            </div>

            {/* Leaflet map */}
            <div className="rounded overflow-hidden border border-ui-border relative" style={{ height: 200 }}>
              <div ref={mapContainerRef} style={{ height: '100%', width: '100%', filter: 'grayscale(10%)' }} />
              {value!.precision === 'precise' && (
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded pointer-events-none z-[1000]">
                  Arrastra el pin para ajustar
                </div>
              )}
            </div>

            {/* Precision toggle */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-ui-text-subtle uppercase tracking-wider">Privacidad de ubicación</span>
              <div className="flex flex-col gap-1">
                {(['precise', 'neighborhood'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPrecision(p)}
                    className={`flex items-start gap-2.5 px-3 py-2.5 rounded border text-left transition-colors ${
                      value!.precision === p
                        ? 'border-ui-accent bg-ui-accent/10'
                        : 'border-ui-border hover:border-ui-border-strong bg-transparent'
                    }`}
                  >
                    <span className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                      value!.precision === p ? 'border-ui-accent' : 'border-ui-text-subtle'
                    }`}>
                      {value!.precision === p && (
                        <span className="w-1.5 h-1.5 rounded-full bg-ui-accent block" />
                      )}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-xs text-ui-text font-medium">
                        {p === 'precise' ? 'Mostrar dirección exacta' : 'Ocultar dirección (privacidad de ubicación)'}
                      </span>
                      <span className="text-xs text-ui-text-subtle">
                        {p === 'precise'
                          ? 'Pin exacto en el mapa y dirección completa visibles'
                          : `Mapa de colonia sin pin — muestra "${value!.areaLabel}"`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Manual override (collapsed) */}
            <button
              type="button"
              onClick={() => setShowManual((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-ui-text-subtle hover:text-ui-text transition-colors self-start"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`transition-transform ${showManual ? 'rotate-180' : ''}`}>
                <path d="m6 9 6 6 6-6"/>
              </svg>
              Editar coordenadas manualmente
            </button>

            <AnimatePresence>
              {showManual && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.16 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center bg-ui-bg border border-ui-border rounded divide-x divide-ui-border">
                    <div className="flex-1 flex flex-col px-3 py-1.5 min-w-0">
                      <span className="text-xs text-ui-text-subtle uppercase tracking-wider mb-1">Latitud</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={value!.lat}
                        onChange={(e) => {
                          const n = parseCoord(e.target.value);
                          if (n != null) onChange({ ...value!, lat: n });
                        }}
                        className="w-full bg-transparent text-sm text-ui-text focus:outline-none"
                      />
                    </div>
                    <span className="px-2 text-ui-text-subtle text-sm select-none shrink-0">,</span>
                    <div className="flex-1 flex flex-col px-3 py-1.5 min-w-0">
                      <span className="text-xs text-ui-text-subtle uppercase tracking-wider mb-1">Longitud</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={value!.lng}
                        onChange={(e) => {
                          const n = parseCoord(e.target.value);
                          if (n != null) onChange({ ...value!, lng: n });
                        }}
                        className="w-full bg-transparent text-sm text-ui-text focus:outline-none"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
